/**
 * SOUND-657 — one definition of how a MID's monthly volume cap reads.
 *
 * `monthlyVolumeCap: null` used to mean two different things: "this MID has no
 * cap" and "nobody entered one". Every surface rendered both as "Unlimited", so
 * 14 of 256 MIDs claimed to be uncapped when the field was simply blank —
 * which, for a field underwriting reads, is the wrong direction to be wrong in.
 *
 * They are separate now: `isUncapped` is the state, `monthlyVolumeCap` is the
 * number, and a blank number is $0 — not a licence to process.
 *
 * "Uncapped" rather than "Unlimited" to match how processors actually talk about
 * it.
 */

export const UNCAPPED_LABEL = "Uncapped";

/** The cap as it should read anywhere in the UI. */
export function formatVolumeCap(mid, { currency = true } = {}) {
  if (!mid) return "—";
  if (mid.isUncapped) return UNCAPPED_LABEL;

  const amount = Number(mid.monthlyVolumeCap) || 0;
  return currency ? `$${amount.toLocaleString()}` : String(amount);
}

/**
 * The value for a CSV cell. Same rule, but no currency symbol or thousands
 * separators — a spreadsheet should get a number it can sum, and the one
 * non-numeric case has to be a word rather than a misleading 0.
 */
export function volumeCapExportValue(mid) {
  if (!mid) return "";
  return mid.isUncapped ? UNCAPPED_LABEL : String(Number(mid.monthlyVolumeCap) || 0);
}

/**
 * Normalise an edit form's cap fields into an API payload.
 *
 * The two fields are mutually exclusive, so this settles them before they leave
 * the client. The server reconciles them again — this is convenience, not the
 * enforcement.
 */
export function volumeCapPayload({ isUncapped, monthlyVolumeCap }) {
  if (isUncapped) return { isUncapped: true, monthlyVolumeCap: null };

  const raw = String(monthlyVolumeCap ?? "").trim();
  return {
    isUncapped: false,
    // A blank input is 0, not null. Null is what created the ambiguity.
    monthlyVolumeCap: raw === "" ? 0 : Number(raw),
  };
}
